import React from 'react';
import { useGodModeAuth } from '../contexts/GodModeAuthContext';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { LogOut, Shield, Bug } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Badge } from '@/components/ui/badge';

export const GodModeHeader: React.FC = () => {
  const { user, logout, token } = useGodModeAuth();
  const navigate = useNavigate();

  const { data: openBugs } = useQuery({
    queryKey: ['gm-open-bugs-count'],
    enabled: !!token,
    refetchInterval: 30000,
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('godmode-bug-reports/stats', {
        method: 'GET',
        headers: { 'x-godmode-token': token! },
      });
      if (error) return 0;
      return (data as any)?.stats?.ABERTO ?? 0;
    },
  });

  const handleLogout = async () => {
    await logout();
    navigate('/godmode/auth');
  };


  const initials = user?.full_name
    ?.split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase() || 'GM';

  return (
    <header className="h-16 border-b border-slate-800 bg-slate-900/50 backdrop-blur-sm flex items-center justify-between px-6">
      <div className="flex items-center gap-2">
        <Shield className="w-5 h-5 text-amber-500" />
        <span className="text-sm text-amber-500 font-medium">
          Modo Administrador Master
        </span>
      </div>

      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/godmode/bug-reports')}
          className="relative text-slate-300 hover:text-amber-400 hover:bg-slate-800"
          title="Bugs reportados"
        >
          <Bug className="h-4 w-4 mr-2" />
          <span className="text-xs">Bugs</span>
          {openBugs && openBugs > 0 ? (
            <Badge className="ml-2 bg-red-600 text-white h-5 px-1.5 text-[10px]">{openBugs}</Badge>
          ) : null}
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="relative h-10 w-10 rounded-full">
              <Avatar className="h-10 w-10 border-2 border-cyan-500/50">
                <AvatarFallback className="bg-cyan-500/20 text-cyan-400">
                  {initials}
                </AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56 bg-slate-900 border-slate-700" align="end">
            <DropdownMenuLabel className="text-slate-300">
              <div className="flex flex-col space-y-1">
                <p className="text-sm font-medium">{user?.full_name}</p>
                <p className="text-xs text-slate-500">{user?.email}</p>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator className="bg-slate-700" />
            <DropdownMenuItem 
              className="text-slate-300 focus:bg-slate-800 focus:text-slate-200 cursor-pointer"
              onClick={handleLogout}
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sair
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};
